import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Plus, Loader2, Trash2, Pencil, ExternalLink, ListChecks, ChevronRight, EyeOff, Trophy, Star, Tag } from "lucide-react";
import LocationScoreboard from "@/components/LocationScoreboard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { locationScore } from "@/lib/helpers";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const EMPTY = { name: "", address: "", place_id: null, lat: null, lng: null, maps_url: null };

export default function Locations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = user?.role === "admin" || user?.role === "manager";
  const [showRejected, setShowRejected] = useState(false);
  const [showScoreboard, setShowScoreboard] = useState(false);
  const [locations, setLocations] = useState([]);
  const [elements, setElements] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [applyPlaybook, setApplyPlaybook] = useState(true);
  const [playbookCount, setPlaybookCount] = useState(0);

  const load = useCallback(async () => {
    const [l, pb, se, lc] = await Promise.all([api.get("/locations"), api.get("/playbook-tasks"), api.get("/scorecard-elements"), api.get("/location-categories")]);
    setLocations(l.data); setPlaybookCount(pb.data.length); setElements(se.data); setCats(lc.data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const catName = (id) => cats.find((c) => c.id === id)?.name;

  const openNew = () => { setEditing(null); setForm(EMPTY); setApplyPlaybook(true); setOpen(true); };
  const openEdit = (l) => { setEditing(l.id); setForm({ name: l.name, address: l.address || "", place_id: l.place_id || null, lat: l.lat ?? null, lng: l.lng ?? null, maps_url: l.maps_url || null }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Location name is required");
    setSaving(true);
    try {
      if (editing) await api.patch(`/locations/${editing}`, form);
      else await api.post("/locations", { ...form, apply_playbook: applyPlaybook });
      toast.success(editing ? "Location updated" : (applyPlaybook && playbookCount ? `Location added with ${playbookCount} playbook task(s)` : "Location added"));
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const runPlaybook = async (id) => {
    try {
      const { data } = await api.post(`/locations/${id}/apply-playbook`);
      toast.success(data.created ? `Created ${data.created} playbook task(s)` : "No playbook tasks defined yet");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this location?")) return;
    try { await api.delete(`/locations/${id}`); toast.success("Location deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.15em] text-[#b32233]">Store network</p>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-900">Locations</h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowScoreboard((v) => !v)} data-testid="toggle-scoreboard" className={`flex items-center gap-1.5 border px-3 py-2 text-xs font-medium transition-colors ${showScoreboard ? "border-[#b32233] bg-[#b32233] text-white" : "border-zinc-200 text-zinc-600 hover:border-zinc-300"}`}>
            <Trophy className="h-3.5 w-3.5" /> Scoreboard
          </button>
          <button onClick={() => setShowRejected((v) => !v)} data-testid="toggle-rejected" className={`flex items-center gap-1.5 border px-3 py-2 text-xs font-medium transition-colors ${showRejected ? "border-[#b32233] bg-[#b32233] text-white" : "border-zinc-200 text-zinc-600 hover:border-zinc-300"}`}>
            <EyeOff className="h-3.5 w-3.5" /> {showRejected ? "Hiding none" : "Show rejected"}
          </button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="add-location-button" onClick={openNew} className="flex items-center gap-2 bg-[#b32233] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#8f1a28]">
              <Plus className="h-4 w-4" /> New Location
            </button>
          </DialogTrigger>
          <DialogContent
            className="rounded-none sm:max-w-md"
            onPointerDownOutside={(e) => { const t = e.detail?.originalEvent?.target; if (t?.closest?.(".pac-container")) e.preventDefault(); }}
            onInteractOutside={(e) => { const t = e.detail?.originalEvent?.target; if (t?.closest?.(".pac-container")) e.preventDefault(); }}
          >
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">{editing ? "Edit location" : "Add location"}</DialogTitle>
              <DialogDescription>Store locations where approved products and equipment are stocked.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <input data-testid="location-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Location name" className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#b32233]" />
              <AddressAutocomplete
                testid="location-address-input"
                value={form.address}
                placeholder="Start typing an address…"
                onChange={(v) => setForm((f) => ({ ...f, address: v, place_id: null, lat: null, lng: null, maps_url: null }))}
                onSelect={(sel) => setForm((f) => ({ ...f, ...sel }))}
              />
              {form.maps_url && <a href={form.maps_url} target="_blank" rel="noreferrer" data-testid="location-form-maps-link" className="flex items-center gap-1 text-xs font-medium text-[#b32233] hover:underline"><ExternalLink className="h-3 w-3" /> Verify on Google Maps</a>}
              {!editing && (
                <label className="flex items-center gap-2 border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-700" data-testid="apply-playbook-checkbox-label">
                  <input type="checkbox" data-testid="apply-playbook-checkbox" checked={applyPlaybook} onChange={(e) => setApplyPlaybook(e.target.checked)} disabled={playbookCount === 0} />
                  <span className="flex items-center gap-1"><ListChecks className="h-3.5 w-3.5 text-[#b32233]" /> Create playbook tasks for this location {playbookCount > 0 ? `(${playbookCount})` : "(none defined)"}</span>
                </label>
              )}
            </div>
            <DialogFooter>
              <button data-testid="location-submit" onClick={save} disabled={saving} className="flex items-center gap-2 bg-[#b32233] px-4 py-2 text-sm font-medium text-white hover:bg-[#8f1a28] disabled:opacity-60">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Save changes" : "Add location"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {!loading && showScoreboard && <LocationScoreboard locations={locations} elements={elements} />}

      {loading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#b32233]" /></div>
      ) : locations.length === 0 ? (
        <div className="flex h-64 items-center justify-center text-sm text-zinc-400">No locations yet. Add your first store.</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {locations.filter((l) => showRejected || (l.status || "potential") !== "rejected").map((l) => {
            const isPotential = (l.status || "potential") === "potential";
            const { score, hasScore } = locationScore(l, elements);
            return (
              <div key={l.id} className="border border-zinc-200 bg-white" data-testid={`location-card-${l.id}`}>
                <div className="flex items-start justify-between border-b border-zinc-200 p-4">
                  <div>
                    <button onClick={() => navigate(`/locations/${l.id}`)} data-testid={`open-location-${l.id}`} className="flex items-center gap-2 font-heading text-base font-semibold tracking-tight text-zinc-900 hover:text-[#b32233]"><MapPin className="h-4 w-4 text-[#b32233]" /> {l.name} <ChevronRight className="h-4 w-4 text-zinc-300" /></button>
                    <span className="mt-1 inline-block border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-zinc-500" data-testid={`location-status-${l.id}`}>{(l.status || "potential").replace("_", " ")}</span>
                    {l.address && (l.maps_url ? (
                      <a href={l.maps_url} target="_blank" rel="noreferrer" data-testid={`location-maps-link-${l.id}`} className="mt-1 flex items-center gap-1 text-xs text-[#b32233] hover:underline">{l.address} <ExternalLink className="h-3 w-3" /></a>
                    ) : (
                      <p className="mt-1 text-xs text-zinc-500">{l.address}</p>
                    ))}
                  </div>
                  <div className="flex items-center gap-1">
                    {canManage && <button onClick={() => runPlaybook(l.id)} data-testid={`apply-playbook-${l.id}`} title="Create playbook tasks for this location" className="p-1 text-zinc-400 hover:text-[#b32233]"><ListChecks className="h-4 w-4" /></button>}
                    <button onClick={() => openEdit(l)} data-testid={`edit-location-${l.id}`} className="p-1 text-zinc-400 hover:text-[#b32233]"><Pencil className="h-4 w-4" /></button>
                    {canManage && <button onClick={() => del(l.id)} data-testid={`delete-location-${l.id}`} className="p-1 text-zinc-400 hover:text-[#b32233]"><Trash2 className="h-4 w-4" /></button>}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-x-8 gap-y-3 p-4">
                  <div className="flex items-center gap-2" data-testid={`location-category-${l.id}`}>
                    <Tag className="h-3.5 w-3.5 text-zinc-400" />
                    <span className="text-xs uppercase tracking-wider text-zinc-500">Category</span>
                    <span className="text-sm font-medium text-zinc-800">{catName(l.category_id) || "—"}</span>
                  </div>
                  {isPotential && (
                    <div className="flex items-center gap-2" data-testid={`location-score-${l.id}`}>
                      <Star className="h-3.5 w-3.5 text-[#b32233]" />
                      <span className="text-xs uppercase tracking-wider text-zinc-500">Score</span>
                      <span className="font-heading text-base font-bold text-[#b32233]">{hasScore ? `${score.toFixed(1)} / 10` : "—"}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
